// Generic recursive editor for arbitrary JSON values (objects/arrays/primitives).
// Used to render a visual "form" on top of any config JSON without assuming a fixed schema,
// so it works for every config type (Meta Data, Home Page, Footer, Custom, Logic, Notification Bar).
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faArrowUp, faArrowDown, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

function getValueType(value) {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value; // 'object' | 'string' | 'number' | 'boolean'
}

function defaultValueForType(type) {
    switch (type) {
        case 'number': return 0;
        case 'boolean': return false;
        case 'array': return [];
        case 'object': return {};
        default: return '';
    }
}

function cloneValue(value) {
    return (value === undefined) ? value : JSON.parse(JSON.stringify(value));
}

const LAYOUT_OPTIONS = ['1x1', '1x2', '1x3', '1x4'];

function AddFieldForm({ onAdd, disabled }) {
    const [key, setKey] = useState('');
    const [type, setType] = useState('string');

    if (disabled) return null;

    const submit = () => {
        const trimmed = key.trim();
        if (!trimmed) return;
        onAdd(trimmed, defaultValueForType(type));
        setKey('');
    }

    return (
        <div className="d-flex gap-2 align-items-center mt-2 flex-wrap">
            <input type="text" className="form-control form-control-sm" style={{ maxWidth: '180px' }} placeholder="New field name" value={key} onChange={(e) => setKey(e.target.value)} />
            <select className="form-control form-control-sm" style={{ maxWidth: '130px' }} value={type} onChange={(e) => setType(e.target.value)}>
                <option value="string">Text</option>
                <option value="number">Number</option>
                <option value="boolean">True/False</option>
                <option value="object">Group</option>
                <option value="array">List</option>
            </select>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={submit}>
                <FontAwesomeIcon icon={faPlus} /> Add Field
            </button>
        </div>
    )
}

function ArrayItemEditor({ item, idx, count, depth, disabled, onMoveUp, onMoveDown, onRemove, onChange }) {
    const [Collapsed, setCollapsed] = useState(false);

    return (
        <div className="ms-2 ps-2 border-start mb-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="d-flex align-items-center gap-2">
                    <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none" onClick={() => setCollapsed(!Collapsed)} title={Collapsed ? 'Expand' : 'Collapse'}>
                        <FontAwesomeIcon icon={Collapsed ? faChevronRight : faChevronDown} />
                    </button>
                    <span className="fs-12rem fw-bold text-muted">Item #{idx + 1}</span>
                </div>
                {!disabled ? (
                    <div className="d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" disabled={idx === 0} title="Move up" onClick={onMoveUp}><FontAwesomeIcon icon={faArrowUp} /></button>
                        <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" disabled={idx === count - 1} title="Move down" onClick={onMoveDown}><FontAwesomeIcon icon={faArrowDown} /></button>
                        <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" title="Remove item" onClick={onRemove}><FontAwesomeIcon icon={faTrash} /></button>
                    </div>
                ) : null}
            </div>
            {!Collapsed ? (
                <JsonNodeEditor
                    value={item}
                    depth={depth + 1}
                    disabled={disabled}
                    onChange={onChange}
                />
            ) : null}
        </div>
    )
}

function JsonNodeEditor({ value, onChange, label, depth = 0, onRemove, disabled }) {
    const type = getValueType(value);
    const [Collapsed, setCollapsed] = useState(false);

    const headerRow = (label !== undefined) ? (
        <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label fs-13rem fw-bold mb-0">{label}</label>
            {(onRemove && !disabled) ? (
                <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={onRemove} title="Remove field">
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            ) : null}
        </div>
    ) : null;

    // Header used for collapsible containers (objects/arrays) so a nested tree can be closed while editing.
    const collapsibleHeader = (labelNode, count, noun) => (
        <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="d-flex align-items-center gap-2">
                <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none" onClick={() => setCollapsed(!Collapsed)} title={Collapsed ? 'Expand' : 'Collapse'}>
                    <FontAwesomeIcon icon={Collapsed ? faChevronRight : faChevronDown} />
                </button>
                {labelNode}
                {Collapsed ? <span className="text-muted fs-12rem">({count} {noun}{count === 1 ? '' : 's'})</span> : null}
            </div>
            {(onRemove && !disabled) ? (
                <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={onRemove} title="Remove field">
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            ) : null}
        </div>
    );

    if (type === 'object') {
        const entries = Object.entries(value);
        const labelNode = (label !== undefined) ? <label className="form-label fs-13rem fw-bold mb-0">{label}</label> : null;
        return (
            <div className="border rounded p-2 mb-2 bg-white">
                {collapsibleHeader(labelNode, entries.length, 'field')}
                {!Collapsed ? <>
                    {entries.length === 0 ? <div className="text-muted fs-12rem mb-1">No fields yet</div> : null}
                    {entries.map(([k, v]) => (
                        <div key={k} className="ms-2 ps-2 border-start">
                            <JsonNodeEditor
                                value={v}
                                label={k}
                                depth={depth + 1}
                                disabled={disabled}
                                onChange={(newVal) => onChange({ ...value, [k]: newVal })}
                                onRemove={() => {
                                    const updated = { ...value };
                                    delete updated[k];
                                    onChange(updated);
                                }}
                            />
                        </div>
                    ))}
                    <AddFieldForm disabled={disabled} onAdd={(newKey, newVal) => {
                        if (Object.prototype.hasOwnProperty.call(value, newKey)) return;
                        onChange({ ...value, [newKey]: newVal });
                    }} />
                </> : null}
            </div>
        )
    }

    if (type === 'array') {
        const labelNode = (label !== undefined) ? <label className="form-label fs-13rem fw-bold mb-0">{label}</label> : null;
        return (
            <div className="border rounded p-2 mb-2 bg-white">
                {collapsibleHeader(labelNode, value.length, 'item')}
                {!Collapsed ? <>
                    {value.length === 0 ? <div className="text-muted fs-12rem mb-1">No items yet</div> : null}
                    {value.map((item, idx) => (
                        <ArrayItemEditor
                            key={idx}
                            item={item}
                            idx={idx}
                            count={value.length}
                            depth={depth}
                            disabled={disabled}
                            onMoveUp={() => {
                                const updated = [...value];
                                [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                                onChange(updated);
                            }}
                            onMoveDown={() => {
                                const updated = [...value];
                                [updated[idx + 1], updated[idx]] = [updated[idx], updated[idx + 1]];
                                onChange(updated);
                            }}
                            onRemove={() => onChange(value.filter((_, i) => i !== idx))}
                            onChange={(newVal) => {
                                const updated = [...value];
                                updated[idx] = newVal;
                                onChange(updated);
                            }}
                        />
                    ))}
                    {!disabled ? (
                        <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={() => {
                            const cloneOfLast = value.length > 0 ? cloneValue(value[value.length - 1]) : '';
                            onChange([...value, cloneOfLast]);
                        }}>
                            <FontAwesomeIcon icon={faPlus} /> Add Item
                        </button>
                    ) : null}
                </> : null}
            </div>
        )
    }

    if (type === 'boolean') {
        return (
            <div className="mb-2">
                {headerRow}
                <select className="form-control form-control-sm" value={value ? 'true' : 'false'} disabled={disabled}
                    onChange={(e) => onChange(e.target.value === 'true')}>
                    <option value="true">True</option>
                    <option value="false">False</option>
                </select>
            </div>
        )
    }

    if (type === 'number') {
        return (
            <div className="mb-2">
                {headerRow}
                <input type="number" className="form-control form-control-sm" value={value} disabled={disabled}
                    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
        )
    }

    if (type === 'null') {
        return (
            <div className="mb-2">
                {headerRow}
                <div className="d-flex gap-2 align-items-center">
                    <span className="text-muted fs-12rem">(empty)</span>
                    {!disabled ? (
                        <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => onChange('')}>Set Value</button>
                    ) : null}
                </div>
            </div>
        )
    }

    // string (default)
    const stringValue = (typeof value === 'string') ? value : '';

    if (label === 'layout') {
        return (
            <div className="mb-2">
                {headerRow}
                <select className="form-control form-control-sm" value={stringValue} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}>
                    <option value="">Choose...</option>
                    {LAYOUT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
        )
    }

    const isLong = stringValue.length > 60 || stringValue.includes('\n');
    return (
        <div className="mb-2">
            {headerRow}
            {isLong ? (
                <textarea className="form-control form-control-sm" rows={3} value={stringValue} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)} />
            ) : (
                <input type="text" className="form-control form-control-sm" value={stringValue} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)} />
            )}
        </div>
    )
}

export default JsonNodeEditor;
